import { useSyncExternalStore } from 'react'
import type { AgentChatProject, AgentChatSessionSummary } from '@/frontend/pages/Dashboard/AgentChats/types'
import type { ContextUsage } from '@/frontend/features/AgentChats/events/agentEvents'

// Cross-layout bridge (SZ-1504). The persistent `DashboardLayout` renders the
// Chat-mode sidebar (agent picker + session list) but lives OUTSIDE the
// AgentChats page's React subtree, so React context can't reach it. The mounted
// AgentChats page is the single source of truth for the live session list
// (kept in sync by `useAgentChatsChannel`); it publishes a snapshot here and the
// layout subscribes via `useSyncExternalStore`. When the page unmounts it
// publishes `null` so the layout falls back to the empty "escolha um agente"
// state instead of showing a dead worker's sessions.
export interface AgentChatSidebarSnapshot {
  activeWorkerId: string | null
  workerName: string | null
  activeSessionId: string | null
  isNewConversation: boolean
  canStartNewConversation: boolean
  contextUsage: ContextUsage | null
  sessions: AgentChatSessionSummary[]
  projects: AgentChatProject[]
  onNewConversation: () => void
  onRenameSession: (sessionId: string, title: string) => void
  onPinSession: (sessionId: string, pinned: boolean) => void
  onArchiveSession: (sessionId: string) => void
  onDeleteSession: (sessionId: string) => void
  onMoveSessionToProject: (sessionId: string, projectId: string | null) => void
  onCreateProject: (name: string) => void
  onRenameProject: (projectId: string, name: string) => void
  onDeleteProject: (projectId: string) => void
}

let currentSnapshot: AgentChatSidebarSnapshot | null = null
const listeners = new Set<() => void>()

function sessionsEqual(a: AgentChatSessionSummary[], b: AgentChatSessionSummary[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].id !== b[i].id ||
      a[i].title !== b[i].title ||
      a[i].last_message_at !== b[i].last_message_at ||
      a[i].pinned !== b[i].pinned ||
      a[i].project_id !== b[i].project_id ||
      a[i].project_name !== b[i].project_name ||
      a[i].fde_project_id !== b[i].fde_project_id ||
      a[i].fde_project_name !== b[i].fde_project_name ||
      !parallelWorkEqual(a[i].parallel_work, b[i].parallel_work)
    ) {
      return false
    }
  }
  return true
}

function parallelWorkEqual(
  a: AgentChatSessionSummary['parallel_work'],
  b: AgentChatSessionSummary['parallel_work'],
): boolean {
  if (a === b) return true
  if ((a?.length || 0) !== (b?.length || 0)) return false
  return (a || []).every((item, index) => (
    item.id === b[index].id &&
    item.title === b[index].title &&
    item.status === b[index].status &&
    item.run_id === b[index].run_id &&
    item.turn_id === b[index].turn_id
  ))
}

function projectsEqual(a: AgentChatProject[], b: AgentChatProject[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((project, index) => (
    project.id === b[index].id &&
    project.name === b[index].name &&
    project.position === b[index].position
  ))
}

// Skip no-op publishes so `getSnapshot` stays referentially stable when nothing
// changed. The page's `sessions` array is re-created with identical content on
// some renders; without this guard the store would churn and React would warn
// ("getSnapshot should be cached to avoid an infinite loop").
function snapshotsEqual(a: AgentChatSidebarSnapshot | null, b: AgentChatSidebarSnapshot | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.activeWorkerId === b.activeWorkerId &&
    a.workerName === b.workerName &&
    a.activeSessionId === b.activeSessionId &&
    a.isNewConversation === b.isNewConversation &&
    a.canStartNewConversation === b.canStartNewConversation &&
    a.contextUsage === b.contextUsage &&
    a.onNewConversation === b.onNewConversation &&
    a.onRenameSession === b.onRenameSession &&
    a.onPinSession === b.onPinSession &&
    a.onArchiveSession === b.onArchiveSession &&
    a.onDeleteSession === b.onDeleteSession &&
    a.onMoveSessionToProject === b.onMoveSessionToProject &&
    a.onCreateProject === b.onCreateProject &&
    a.onRenameProject === b.onRenameProject &&
    a.onDeleteProject === b.onDeleteProject &&
    sessionsEqual(a.sessions, b.sessions) &&
    projectsEqual(a.projects, b.projects)
  )
}

export function publishAgentChatSidebar(snapshot: AgentChatSidebarSnapshot | null): void {
  if (snapshotsEqual(currentSnapshot, snapshot)) return
  currentSnapshot = snapshot
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Stable identity between publishes so useSyncExternalStore doesn't loop.
function getSnapshot(): AgentChatSidebarSnapshot | null {
  return currentSnapshot
}

export function useAgentChatSidebar(): AgentChatSidebarSnapshot | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

// One-shot read for event handlers that must not subscribe/re-render — the
// worker-first ⌘N action (SZ-1603, B.2) reads the store at keypress time to
// reuse the mounted chat page's in-page reset when it is publishing.
export function readAgentChatSidebar(): AgentChatSidebarSnapshot | null {
  return currentSnapshot
}
